
import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * IndexedDB utility functions for offline-first data storage
 */

// Use timestamp-based versioning for better cross-deployment compatibility
const DB_NAME = "jimatpos_db";
const DB_VERSION = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
const USER_STORE = "users";
const STORE_SETTINGS = "store_settings";
const VOUCHER_STORE = "vouchers";
const VOUCHER_HISTORY_STORE = "voucher_history";
const VOUCHER_TYPES_STORE = "voucher_types";
const VOUCHER_SETTINGS_STORE = "voucher_settings";
const SUBSIDY_ALLOCATION_STORE = "subsidy_allocations";
const BENEFICIARIES_STORE = "subsidy_beneficiaries";
const SUBSIDY_USAGE_STORE = "subsidy_usage";
const SUBSIDY_SETTINGS_STORE = "subsidy_settings";
const SYNC_LOG_STORE = "sync_logs";
const PRODUCT_STORE = "products";

// Export the DB version for display purposes
export const getDatabaseVersion = () => DB_VERSION;

interface JimatPOSDB extends DBSchema {
  users: {
    key: string;
    value: User;
    indexes: { 'email': string };
  };
  store_settings: {
    key: string;
    value: any;
    indexes: { 'id': string };
  };
  vouchers: {
    key: string;
    value: any;
    indexes: {
      'customerId': string;
      'status': string;
      'voucherType': string;
      'expiryDate': number;
    };
  };
  voucher_history: {
    key: number;
    value: any;
    indexes: {
      'voucherId': string;
      'redemptionDate': number;
    };
  };
  voucher_types: {
    key: string;
    value: any;
  };
  voucher_settings: {
    key: string;
    value: any;
  };
  subsidy_allocations: {
    key: string;
    value: any;
    indexes: {
      'customerId': string;
      'status': string;
      'subsidyType': string;
      'expiryDate': number;
    };
  };
  subsidy_beneficiaries: {
    key: string;
    value: any;
    indexes: {
      'icNumber': string;
      'memberType': string;
      'incomeBracket': string;
    };
  };
  subsidy_usage: {
    key: string;
    value: any;
    indexes: {
      'beneficiaryId': string;
      'usageDate': number;
      'productCategory': string;
    };
  };
  subsidy_settings: {
    key: string;
    value: any;
  };
  angkasa_members: {
    key: string;
    value: any;
    indexes: {
      'memberId': string;
      'icNumber': string;
      'cooperativeName': string;
    };
  };
  angkasa_promotions: {
    key: string;
    value: any;
    indexes: {
      'cooperativeName': string;
    };
  };
  angkasa_purchases: {
    key: string;
    value: any;
    indexes: {
      'memberId': string;
    };
  };
  sync_logs: {
    key: string;
    value: SyncLog;
    indexes: {
      'timestamp': number;
      'platform': string;
      'status': string;
    };
  };
  products: {
    key: string;
    value: Product;
    indexes: {
      'category': string;
      'needsSync': number;
      'lastModified': number;
    };
  };
}

// Interface for sync logs
export interface SyncLog {
  id: string;
  timestamp: number;
  platform: 'POS' | 'JimatBiz' | 'JimatShop';
  action: 'upload' | 'download' | 'both';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  details?: string;
  totalItems: number;
  processedItems: number;
  errorMessage?: string;
}

// Interface for products
export interface Product {
  id: string;
  name: string;
  sku: string;
  description?: string;
  category: string;
  subcategory?: string;
  brand?: string;
  unitPrice: number;
  costPrice?: number;
  currentStock: number;
  minStock?: number;
  maxStock?: number;
  barcode?: string;
  image?: string;
  isActive: boolean;
  needsSync: boolean;
  lastModified: number;
  syncPlatforms?: {
    pos: boolean;
    jimatbiz: boolean;
    jimatshop: boolean;
  };
}

let dbInstance: IDBPDatabase<JimatPOSDB> | null = null;

export const initDB = async () => {
  if (dbInstance) {
    return dbInstance;
  }

  try {
    console.log(`Opening database ${DB_NAME} with version ${DB_VERSION}`);
    dbInstance = await openDB<JimatPOSDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        console.log(`Database upgrade needed from ${oldVersion} to ${newVersion}, creating/updating stores...`);

        // Create all stores if they don't exist, regardless of version
        // This ensures all stores are created when upgrading from any version
        
        // User and Store Settings
        if (!db.objectStoreNames.contains(USER_STORE)) {
          console.log("Creating user store...");
          const userStore = db.createObjectStore(USER_STORE, { keyPath: "email" });
          userStore.createIndex("email", "email", { unique: true });
        }

        if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
          console.log("Creating store settings store...");
          const settingsStore = db.createObjectStore(STORE_SETTINGS, { keyPath: "id" });
          settingsStore.createIndex("id", "id", { unique: true });
        }

        // Voucher management stores
        if (!db.objectStoreNames.contains(VOUCHER_STORE)) {
          console.log("Creating vouchers store...");
          const voucherStore = db.createObjectStore(VOUCHER_STORE, { keyPath: "voucherId" });
          voucherStore.createIndex("customerId", "customerId", { unique: false });
          voucherStore.createIndex("status", "status", { unique: false });
          voucherStore.createIndex("voucherType", "voucherType", { unique: false });
          voucherStore.createIndex("expiryDate", "expiryDate", { unique: false });
        }

        if (!db.objectStoreNames.contains(VOUCHER_HISTORY_STORE)) {
          console.log("Creating voucher history store...");
          const historyStore = db.createObjectStore(VOUCHER_HISTORY_STORE, { keyPath: "transactionId", autoIncrement: true });
          historyStore.createIndex("voucherId", "voucherId", { unique: false });
          historyStore.createIndex("redemptionDate", "redemptionDate", { unique: false });
        }

        if (!db.objectStoreNames.contains(VOUCHER_TYPES_STORE)) {
          console.log("Creating voucher types store...");
          db.createObjectStore(VOUCHER_TYPES_STORE, { keyPath: "typeId" });
        }

        if (!db.objectStoreNames.contains(VOUCHER_SETTINGS_STORE)) {
          console.log("Creating voucher settings store...");
          db.createObjectStore(VOUCHER_SETTINGS_STORE, { keyPath: "id" });
        }

        // Subsidy management stores
        if (!db.objectStoreNames.contains(SUBSIDY_ALLOCATION_STORE)) {
          console.log("Creating subsidy allocations store...");
          const subsidyAllocationStore = db.createObjectStore(SUBSIDY_ALLOCATION_STORE, { keyPath: "allocationId" });
          subsidyAllocationStore.createIndex("customerId", "customerId", { unique: false });
          subsidyAllocationStore.createIndex("status", "status", { unique: false });
          subsidyAllocationStore.createIndex("subsidyType", "subsidyType", { unique: false });
          subsidyAllocationStore.createIndex("expiryDate", "expiryDate", { unique: false });
        }

        if (!db.objectStoreNames.contains(BENEFICIARIES_STORE)) {
          console.log("Creating subsidy beneficiaries store...");
          const beneficiariesStore = db.createObjectStore(BENEFICIARIES_STORE, { keyPath: "beneficiaryId" });
          beneficiariesStore.createIndex("icNumber", "icNumber", { unique: false });
          beneficiariesStore.createIndex("memberType", "memberType", { unique: false });
          beneficiariesStore.createIndex("incomeBracket", "incomeBracket", { unique: false });
        }

        if (!db.objectStoreNames.contains(SUBSIDY_USAGE_STORE)) {
          console.log("Creating subsidy usage store...");
          const usageStore = db.createObjectStore(SUBSIDY_USAGE_STORE, { keyPath: "usageId" });
          usageStore.createIndex("beneficiaryId", "beneficiaryId", { unique: false });
          usageStore.createIndex("usageDate", "usageDate", { unique: false });
          usageStore.createIndex("productCategory", "productCategory", { unique: false });
        }

        if (!db.objectStoreNames.contains(SUBSIDY_SETTINGS_STORE)) {
          console.log("Creating subsidy settings store...");
          db.createObjectStore(SUBSIDY_SETTINGS_STORE, { keyPath: "id" });
        }

        // ANGKASA member stores
        if (!db.objectStoreNames.contains("angkasa_members")) {
          console.log("Creating ANGKASA members store...");
          const memberStore = db.createObjectStore("angkasa_members", { keyPath: "memberId" });
          memberStore.createIndex("memberId", "memberId", { unique: true });
          memberStore.createIndex("icNumber", "icNumber", { unique: true });
          memberStore.createIndex("cooperativeName", "cooperativeName", { unique: false });
        }

        if (!db.objectStoreNames.contains("angkasa_promotions")) {
          console.log("Creating ANGKASA promotions store...");
          const promotionStore = db.createObjectStore("angkasa_promotions", { keyPath: "id" });
          promotionStore.createIndex("cooperativeName", "cooperativeName", { unique: false });
        }

        if (!db.objectStoreNames.contains("angkasa_purchases")) {
          console.log("Creating ANGKASA purchases store...");
          const purchaseStore = db.createObjectStore("angkasa_purchases", { keyPath: "id" });
          purchaseStore.createIndex("memberId", "memberId", { unique: false });
        }
        
        // Sync logs store
        if (!db.objectStoreNames.contains(SYNC_LOG_STORE)) {
          console.log("Creating sync logs store...");
          const syncLogStore = db.createObjectStore(SYNC_LOG_STORE, { keyPath: "id" });
          syncLogStore.createIndex("timestamp", "timestamp", { unique: false });
          syncLogStore.createIndex("platform", "platform", { unique: false });
          syncLogStore.createIndex("status", "status", { unique: false });
        }
        
        // Products store
        if (!db.objectStoreNames.contains(PRODUCT_STORE)) {
          console.log("Creating products store...");
          const productStore = db.createObjectStore(PRODUCT_STORE, { keyPath: "id" });
          productStore.createIndex("category", "category", { unique: false });
          productStore.createIndex("needsSync", "needsSync", { unique: false });
          productStore.createIndex("lastModified", "lastModified", { unique: false });
        }
      },
      blocked(currentVersion, blockedVersion, event) {
        console.warn('Database blocked due to version change, closing other tabs...');
      },
      blocking(currentVersion, blockedVersion, event) {
        console.warn('Current database version is blocking a newer version...');
        closeDB();
      },
      terminated() {
        console.error('Database connection terminated unexpectedly');
        dbInstance = null;
      }
    });

    // Add event listeners for connection management
    dbInstance.addEventListener('close', () => {
      console.log('Database connection closed');
      dbInstance = null;
    });

    dbInstance.addEventListener('error', (event) => {
      console.error('Database error:', event);
      dbInstance = null;
    });

    return dbInstance;
  } catch (error) {
    console.error("Error initializing database:", error);
    dbInstance = null;
    throw error;
  }
};

export const closeDB = () => {
  if (dbInstance) {
    console.log('Closing database connection...');
    dbInstance.close();
    dbInstance = null;
  }
};

export const deleteAndReinitializeDatabase = async (): Promise<void> => {
  try {
    // Close any existing connection
    if (dbInstance) {
      dbInstance.close();
      dbInstance = null;
    }

    // Delete the database
    console.log(`Deleting database ${DB_NAME}...`);
    await deleteDB(DB_NAME);
    console.log(`Database ${DB_NAME} deleted successfully`);

    // Reinitialize the database
    console.log('Reinitializing database...');
    await initDB();
    console.log('Database reinitialized successfully');
  } catch (error) {
    console.error('Error deleting and reinitializing database:', error);
    throw error;
  }
};

// User operations
export const saveUser = async (user: User): Promise<void> => {
  const db = await initDB();
  const userWithTimestamp = {
    ...user,
    lastModified: new Date().getTime(),
    needsSync: true
  };
  console.log("Saving user to IndexedDB:", userWithTimestamp);
  await db.put(USER_STORE, userWithTimestamp);
  console.log("User saved successfully to IndexedDB");
};

export const getUser = async (email: string): Promise<User | null> => {
  const db = await initDB();
  console.log("Getting user from IndexedDB with email:", email);
  const user = await db.get(USER_STORE, email);
  console.log("User retrieved from IndexedDB:", user);
  return user || null;
};

export const getAllUsers = async (): Promise<User[]> => {
  const db = await initDB();
  const users = await db.getAll(USER_STORE);
  console.log("All users retrieved from IndexedDB:", users);
  return users;
};

export const deleteUser = async (email: string): Promise<void> => {
  const db = await initDB();
  await db.delete(USER_STORE, email);
};

// Store Settings operations
export const saveStoreSettings = async (settings: any): Promise<void> => {
  const db = await initDB();
  const settingsWithMeta = {
    ...settings,
    id: 'store_profile',
    lastModified: new Date().getTime(),
    needsSync: true
  };
  console.log("Saving store settings to IndexedDB:", settingsWithMeta);
  await db.put(STORE_SETTINGS, settingsWithMeta);
  console.log("Store settings saved successfully to IndexedDB");
};

export const getStoreSettings = async (): Promise<any> => {
  const db = await initDB();
  const settings = await db.get(STORE_SETTINGS, 'store_profile');
  console.log("Retrieved store settings from IndexedDB:", settings);
  return settings;
};

// Sync log operations
export const saveSyncLog = async (syncLog: SyncLog): Promise<void> => {
  const db = await initDB();
  console.log("Saving sync log to IndexedDB:", syncLog);
  await db.put(SYNC_LOG_STORE, syncLog);
  console.log("Sync log saved successfully to IndexedDB");
};

export const getSyncLogs = async (
  platform?: 'POS' | 'JimatBiz' | 'JimatShop', 
  limit = 20
): Promise<SyncLog[]> => {
  const db = await initDB();
  if (platform) {
    const index = db.transaction(SYNC_LOG_STORE).store.index('platform');
    return (await index.getAll(platform)).sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
  } else {
    const logs = await db.getAll(SYNC_LOG_STORE);
    return logs.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
  }
};

export const getLastSync = async (platform: 'POS' | 'JimatBiz' | 'JimatShop'): Promise<SyncLog | null> => {
  const db = await initDB();
  const index = db.transaction(SYNC_LOG_STORE).store.index('platform');
  const logs = await index.getAll(platform);
  
  if (logs.length === 0) {
    return null;
  }
  
  // Return the most recent successful sync
  const successfulSyncs = logs
    .filter(log => log.status === 'completed')
    .sort((a, b) => b.timestamp - a.timestamp);
    
  return successfulSyncs[0] || null;
};

// Product operations with sync
export const saveProduct = async (product: Product): Promise<void> => {
  const db = await initDB();
  const now = new Date().getTime();
  
  // Mark for syncing
  const productWithMeta = {
    ...product,
    lastModified: now,
    needsSync: true
  };
  
  console.log("Saving product to IndexedDB:", productWithMeta);
  await db.put(PRODUCT_STORE, productWithMeta);
  
  console.log("Product saved successfully to IndexedDB");
};

export const getProductsNeedingSync = async (): Promise<Product[]> => {
  const db = await initDB();
  const index = db.transaction(PRODUCT_STORE).store.index('needsSync');
  // Use 1 because IndexedDB doesn't have booleans
  return index.getAll(1);
};

export const markProductsSynced = async (productIds: string[]): Promise<void> => {
  const db = await initDB();
  const tx = db.transaction(PRODUCT_STORE, 'readwrite');
  
  for (const id of productIds) {
    const product = await tx.store.get(id);
    if (product) {
      product.needsSync = false;
      await tx.store.put(product);
    }
  }
  
  await tx.done;
  console.log(`Marked ${productIds.length} products as synced`);
};

// Interface definitions
export interface User {
  email: string;
  password: string; // In a real app, this should be hashed
  name?: string;
  lastLogin?: number;
  isAdmin?: boolean;
  needsSync?: boolean;
  lastModified?: number;
}
